import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { formsAssigned } from '#lib/react-query/queries.ts';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useFormWidget } from '../use-form-widget.ts';
import { StatBase } from './StatBase.tsx';

const widgetTitle = 'Forms Assigned';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows the total number of forms&nbsp;
        <InfoEmphasis>assigned</InfoEmphasis>
        &nbsp;within the selected time period, with a comparison to the previous period of equal
        length. This helps track form distribution volume and monitor changes in workflow demand
        over time.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Forms Assigned =" indented>
        <InfoList
          items={[
            'Count of all forms that were assigned within the selected date range',
            'Excludes forms that were deleted',
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'All forms assigned in the selected date range',
            'Forms not deleted',
            'Forms matching applied date and other assessment filters',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Forms assigned outside the selected date range', 'Deleted forms']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Counts include both required and optional forms (filters may be applied to view one or the other)',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function FormsAssigned() {
  const widget = useFormWidget();

  const { data, isLoading } = useQuery(formsAssigned(widget.getCommonRequestParams()));

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'forms-assigned',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'absolute',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
